/**
 * Galerie webu – správa fotogalerie veřejného webu.
 * Alba = složky v bucketu web-media/gallery/. Fotky nahrané zde se ihned
 * zobrazí na veřejném webu (amatiband.ozvuceniplzen.cz, sekce Média).
 *
 * Supabase Storage nepovoluje v názvech složek diakritiku, proto:
 *   - složka alba má „bezpečný" klíč (slug, např. zacatky-1998),
 *   - zobrazovaný název (s diakritikou) je ve web_settings.gallery_names.
 * Pořadí alb je ve web_settings.gallery_order (pole klíčů).
 * Pořadí fotek v albech je ve web_settings.gallery_photo_order.
 */
import { supabase } from '../lib/supabase.js';
import { compressImage } from '../lib/image-compress.js';
import { rotateStoredImage } from '../lib/image-rotate.js';

const BUCKET = 'web-media';
const ROOT = 'gallery';
const IMG_RE = /\.(jpe?g|png|webp|gif|avif)$/i;

// Cache-bust pro náhledy — po otočení fotky se nastaví, ať se zobrazí nová verze.
let photoBust = '';

function esc(s) {
    if (s == null) return '';
    return String(s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function publicUrl(path) {
    return supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
}

// Bezpečný klíč složky z názvu alba (bez diakritiky a speciálních znaků).
function slugify(s) {
    return (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '')
        .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'album';
}

async function listPhotos(album) {
    const { data } = await supabase.storage.from(BUCKET)
        .list(`${ROOT}/${album}`, { limit: 500, sortBy: { column: 'name', order: 'asc' } });
    return (data || []).filter((e) => e.id !== null && IMG_RE.test(e.name)).map((e) => e.name);
}

async function getOrder() {
    const { data } = await supabase.from('web_settings')
        .select('value').eq('key', 'gallery_order').maybeSingle();
    return Array.isArray(data?.value) ? data.value : [];
}
async function saveOrder(names) {
    const { error } = await supabase.from('web_settings')
        .upsert({ key: 'gallery_order', value: names, updated_at: new Date().toISOString() });
    if (error) console.error('saveOrder:', error.message);
}

// Mapa zobrazovaných názvů alb: { "<klíč složky>": "Pěkný název" }.
async function getNames() {
    const { data } = await supabase.from('web_settings')
        .select('value').eq('key', 'gallery_names').maybeSingle();
    const v = data && data.value;
    return (v && typeof v === 'object' && !Array.isArray(v)) ? v : {};
}
async function saveNames(map) {
    const { error } = await supabase.from('web_settings')
        .upsert({ key: 'gallery_names', value: map, updated_at: new Date().toISOString() });
    if (error) console.error('saveNames:', error.message);
}

// Pořadí fotek uvnitř alb: { "<klíč alba>": ["soubor1",…] }.
async function getPhotoOrder() {
    const { data } = await supabase.from('web_settings')
        .select('value').eq('key', 'gallery_photo_order').maybeSingle();
    const v = data && data.value;
    return (v && typeof v === 'object' && !Array.isArray(v)) ? v : {};
}
async function savePhotoOrder(map) {
    const { error } = await supabase.from('web_settings')
        .upsert({ key: 'gallery_photo_order', value: map, updated_at: new Date().toISOString() });
    if (error) console.error('savePhotoOrder:', error.message);
}

function orderBy(items, arr, keyFn) {
    const idx = new Map((arr || []).map((n, i) => [n, i]));
    return items.slice().sort((a, b) =>
        (idx.has(keyFn(a)) ? idx.get(keyFn(a)) : 1e9) - (idx.has(keyFn(b)) ? idx.get(keyFn(b)) : 1e9));
}

export async function renderGalerieWebu(container) {
    container.innerHTML = '<div class="state-banner">Načítám…</div>';

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
        container.innerHTML = '<div class="state-banner">Nejdřív se přihlas.</div>';
        return;
    }

    const { data, error } = await supabase.storage.from(BUCKET)
        .list(ROOT, { limit: 200, sortBy: { column: 'name', order: 'asc' } });
    if (error) {
        container.innerHTML = `<div class="state-banner error">Chyba: ${esc(error.message)}</div>`;
        return;
    }

    const folders = (data || []).filter((e) => e.id === null && !e.name.startsWith('.'));
    const names = await getNames();
    const photoOrder = await getPhotoOrder();
    let albums = [];
    for (const f of folders) {
        const photos = await listPhotos(f.name);
        // Úvodní fotka = první podle uživatelského pořadí (stejně jako veřejný web).
        const ordered = orderBy(photos.map((n) => ({ name: n })), photoOrder[f.name], (x) => x.name);
        albums.push({
            key: f.name,
            name: names[f.name] || f.name,
            count: photos.length,
            cover: ordered[0]?.name || null
        });
    }
    const order = await getOrder();
    albums = orderBy(albums, order, (a) => a.key);

    container.innerHTML = `
        <div class="filters">
            <h2 style="font-size:18px; margin-right:auto;">Galerie webu</h2>
            <button class="btn btn-sm btn-primary" id="new-album">+ Nové album</button>
        </div>
        <p style="font-size:13px; color:var(--text-muted); margin-bottom:14px;">
            Alba a fotky se ihned zobrazí na veřejném webu (Média). Pořadí změníš
            přetažením alba myší, název upravíš dvojklikem na něj.
        </p>
        ${albums.length === 0
            ? '<div class="state-banner">Zatím žádná alba. Vytvoř první tlačítkem „+ Nové album".</div>'
            : `<div id="album-grid" style="display:grid; grid-template-columns:repeat(auto-fill,minmax(220px,1fr)); gap:14px;">
                ${albums.map((a) => albumCard(a)).join('')}
               </div>`}
    `;

    container.querySelector('#new-album').addEventListener('click', async () => {
        const name = (prompt('Název nového alba:') || '').trim();
        if (!name) return;
        // Bezpečný, jedinečný klíč složky.
        const existing = new Set(folders.map((f) => f.name));
        const base = slugify(name);
        let key = base, n = 2;
        while (existing.has(key)) key = `${base}-${n++}`;

        // .emptyFolderPlaceholder = soubor, kterým Supabase drží prázdnou složku
        // viditelnou. (S vlastním .keep se prázdné album v list() neobjevilo.)
        const { error: e } = await supabase.storage.from(BUCKET)
            .upload(`${ROOT}/${key}/.emptyFolderPlaceholder`,
                new Blob([''], { type: 'text/plain' }), { upsert: true });
        if (e) { alert('Vytvoření alba selhalo: ' + e.message); return; }

        const nm = await getNames();
        nm[key] = name;
        await saveNames(nm);
        const ord = await getOrder();
        if (!ord.includes(key)) { ord.unshift(key); await saveOrder(ord); }
        renderGalerieWebu(container);
    });

    wireAlbumGrid(container, albums);
}

function albumCard(a) {
    const cover = a.cover ? publicUrl(`${ROOT}/${a.key}/${a.cover}`) : '';
    return `
        <div class="gw-album" draggable="true" data-album="${esc(a.key)}"
             style="border:1px solid var(--border); border-radius:10px; overflow:hidden; cursor:grab;">
            <div data-open="${esc(a.key)}"
                 style="aspect-ratio:4/3; cursor:pointer; background:#15151b center/cover no-repeat;
                        ${cover ? `background-image:url('${esc(cover)}')` : ''}"></div>
            <div style="padding:10px 12px;">
                <strong class="gw-name" data-album="${esc(a.key)}" title="Dvojklik = přejmenovat"
                        style="cursor:text;">${esc(a.name)}</strong><br>
                <span style="font-size:12px; color:var(--text-muted);">${a.count} fotek</span>
            </div>
        </div>`;
}

function wireAlbumGrid(container, albums) {
    container.querySelectorAll('[data-open]').forEach((el) => {
        el.addEventListener('click', () => openAlbum(container, el.dataset.open));
    });

    container.querySelectorAll('.gw-name').forEach((el) => {
        el.addEventListener('dblclick', () => startRename(container, el));
    });

    const grid = container.querySelector('#album-grid');
    if (!grid) return;
    let dragKey = null;
    grid.querySelectorAll('.gw-album').forEach((card) => {
        card.addEventListener('dragstart', () => {
            dragKey = card.dataset.album;
            card.style.opacity = '0.4';
        });
        card.addEventListener('dragend', () => { card.style.opacity = ''; });
        card.addEventListener('dragover', (e) => e.preventDefault());
        card.addEventListener('drop', async (e) => {
            e.preventDefault();
            const target = card.dataset.album;
            if (!dragKey || dragKey === target) return;
            const keys = albums.map((a) => a.key);
            const from = keys.indexOf(dragKey);
            const to = keys.indexOf(target);
            if (from < 0 || to < 0) return;
            keys.splice(from, 1);
            keys.splice(to, 0, dragKey);
            await saveOrder(keys);
            renderGalerieWebu(container);
        });
    });
}

function startRename(container, nameEl) {
    const key = nameEl.dataset.album;
    const oldName = nameEl.textContent;
    // Album je „draggable" — kdyby zůstalo, nešlo by psát do políčka.
    const card = nameEl.closest('.gw-album');
    if (card) card.draggable = false;

    const input = document.createElement('input');
    input.type = 'text';
    input.value = oldName;
    input.style.cssText = 'width:100%; font:inherit; padding:2px 4px;';
    nameEl.replaceWith(input);
    input.focus();
    input.select();

    let done = false;
    const commit = async () => {
        if (done) return;
        done = true;
        const newName = input.value.trim();
        if (newName && newName !== oldName) {
            const nm = await getNames();
            nm[key] = newName;
            await saveNames(nm);
        }
        renderGalerieWebu(container);
    };
    input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') { done = true; renderGalerieWebu(container); }
    });
    input.addEventListener('blur', commit);
}

async function openAlbum(container, albumKey) {
    container.innerHTML = '<div class="state-banner">Načítám album…</div>';
    const names = await getNames();
    const display = names[albumKey] || albumKey;
    const files = await listPhotos(albumKey);
    const photoOrder = await getPhotoOrder();
    const photos = orderBy(files.map((n) => ({ name: n })), photoOrder[albumKey], (x) => x.name)
        .map((x) => x.name);

    container.innerHTML = `
        <div class="filters">
            <button class="btn btn-sm" id="back">← Alba</button>
            <h2 style="font-size:18px; margin:0 auto 0 12px;">${esc(display)}</h2>
            <button class="btn btn-sm rep-btn-danger" id="del-album">Smazat album</button>
        </div>
        <label class="btn btn-sm btn-primary" style="display:inline-block; margin-bottom:4px;">
            + Nahrát fotky
            <input type="file" accept="image/*" multiple id="upl" style="display:none;">
        </label>
        <p id="upl-status" style="font-size:13px; color:var(--text-muted); margin:6px 0 14px;"></p>
        ${photos.length === 0
            ? '<div class="state-banner">Album je prázdné. Nahraj fotky tlačítkem výše.</div>'
            : `<p style="font-size:13px; color:var(--text-muted); margin:0 0 10px;">
                   Pořadí fotek změníš přetažením. První fotka slouží jako náhled alba.
               </p>
               <div id="photo-grid" style="display:grid; grid-template-columns:repeat(auto-fill,minmax(140px,1fr)); gap:8px;">
                ${photos.map((p) => photoThumb(albumKey, p)).join('')}
               </div>`}
    `;

    container.querySelector('#back').addEventListener('click', () => renderGalerieWebu(container));

    container.querySelector('#del-album').addEventListener('click', async () => {
        if (!confirm(`Smazat celé album „${display}" včetně všech fotek?`)) return;
        const { data } = await supabase.storage.from(BUCKET)
            .list(`${ROOT}/${albumKey}`, { limit: 1000 });
        const paths = (data || []).map((e) => `${ROOT}/${albumKey}/${e.name}`);
        if (paths.length) await supabase.storage.from(BUCKET).remove(paths);
        await saveOrder((await getOrder()).filter((n) => n !== albumKey));
        const nm = await getNames();
        if (nm[albumKey] !== undefined) { delete nm[albumKey]; await saveNames(nm); }
        const pmap = await getPhotoOrder();
        if (pmap[albumKey] !== undefined) { delete pmap[albumKey]; await savePhotoOrder(pmap); }
        renderGalerieWebu(container);
    });

    container.querySelector('#upl').addEventListener('change', async (e) => {
        const files2 = [...e.target.files];
        if (!files2.length) return;
        const status = container.querySelector('#upl-status');
        let ok = 0, fail = 0;
        for (let i = 0; i < files2.length; i++) {
            status.textContent = `Nahrávám ${i + 1}/${files2.length}…`;
            try {
                const compressed = await compressImage(files2[i], { maxWidth: 1920, quality: 0.85 });
                const ext = (compressed.name.split('.').pop() || 'jpg').toLowerCase();
                const fname = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
                const { error: ue } = await supabase.storage.from(BUCKET)
                    .upload(`${ROOT}/${albumKey}/${fname}`, compressed, {
                        contentType: compressed.type || 'image/jpeg'
                    });
                if (ue) throw ue;
                ok++;
            } catch (ex) {
                fail++;
                console.error('Upload error:', ex);
            }
        }
        status.textContent = `Nahráno: ${ok}${fail ? `, selhalo: ${fail}` : ''}.`;
        openAlbum(container, albumKey);
    });

    container.querySelectorAll('[data-del-photo]').forEach((btn) => {
        btn.addEventListener('click', async () => {
            if (!confirm('Smazat tuto fotku?')) return;
            const { error: de } = await supabase.storage.from(BUCKET)
                .remove([`${ROOT}/${albumKey}/${btn.dataset.delPhoto}`]);
            if (de) { alert('Smazání selhalo: ' + de.message); return; }
            openAlbum(container, albumKey);
        });
    });

    container.querySelectorAll('[data-rotate-photo]').forEach((btn) => {
        btn.addEventListener('click', async () => {
            const path = `${ROOT}/${albumKey}/${btn.dataset.rotatePhoto}`;
            btn.disabled = true;
            btn.textContent = '…';
            try {
                await rotateStoredImage(BUCKET, path, publicUrl(path) + '?t=' + Date.now());
                photoBust = '?t=' + Date.now();   // ať se zobrazí otočená verze
                openAlbum(container, albumKey);
            } catch (e) {
                alert('Otočení se nezdařilo: ' + (e.message || e));
                btn.disabled = false;
                btn.textContent = '↻';
            }
        });
    });

    const pgrid = container.querySelector('#photo-grid');
    if (pgrid) {
        let dragF = null;
        pgrid.querySelectorAll('[data-photo]').forEach((thumb) => {
            thumb.addEventListener('dragstart', () => {
                dragF = thumb.dataset.photo;
                thumb.style.opacity = '0.4';
            });
            thumb.addEventListener('dragend', () => { thumb.style.opacity = ''; });
            thumb.addEventListener('dragover', (e) => e.preventDefault());
            thumb.addEventListener('drop', async (e) => {
                e.preventDefault();
                const target = thumb.dataset.photo;
                if (!dragF || dragF === target) return;
                const names2 = photos.slice();
                const from = names2.indexOf(dragF);
                const to = names2.indexOf(target);
                if (from < 0 || to < 0) return;
                names2.splice(from, 1);
                names2.splice(to, 0, dragF);
                const map = await getPhotoOrder();
                map[albumKey] = names2;
                await savePhotoOrder(map);
                openAlbum(container, albumKey);
            });
        });
    }
}

function photoThumb(albumKey, fileName) {
    const url = publicUrl(`${ROOT}/${albumKey}/${fileName}`) + photoBust;
    const cornerBtn = 'position:absolute; width:26px; height:26px; border:0; border-radius:50%;'
        + ' background:rgba(0,0,0,.65); color:#fff; cursor:pointer; font-size:15px; line-height:1;';
    return `
        <div draggable="true" data-photo="${esc(fileName)}"
             style="position:relative; aspect-ratio:1; border-radius:8px; overflow:hidden; cursor:grab;
                    background:#15151b center/cover no-repeat; background-image:url('${esc(url)}');">
            <button data-rotate-photo="${esc(fileName)}" title="Otočit fotku"
                style="${cornerBtn} top:4px; left:4px;">↻</button>
            <button data-del-photo="${esc(fileName)}" title="Smazat fotku"
                style="${cornerBtn} top:4px; right:4px;">×</button>
        </div>`;
}
